import { useEffect, useState } from 'react';
import { GallerySpacing } from '../components/GallerySpacing';
import { api } from '../lib/api';
import { formatDate, formatUsd } from '../lib/format';

type FundsPolicy = 'never' | 'review' | 'all';

interface OrderReviewSettings {
  review_threshold_cents: number | null;
  funds_policy: FundsPolicy;
  held_ship_window: string;
  updated_at: string | null;
  updated_by: string | null;
}

const FUNDS_OPTIONS: { value: FundsPolicy; label: string; help: string }[] = [
  {
    value: 'review',
    label: 'Only for orders that need approval',
    help: 'Orders at or over the review amount wait for approval and for the payment to clear.',
  },
  {
    value: 'all',
    label: 'For every order',
    help: 'No order goes to Gelato until its payment has cleared in Stripe (usually ~2 business days for cards).',
  },
  {
    value: 'never',
    label: 'Never',
    help: 'Orders go to Gelato as soon as they are paid (and approved, when approval is needed).',
  },
];

export function Settings() {
  const [saved, setSaved] = useState<OrderReviewSettings | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState('');
  const [fundsPolicy, setFundsPolicy] = useState<FundsPolicy>('review');
  const [shipWindow, setShipWindow] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);

  const apply = (s: OrderReviewSettings) => {
    setSaved(s);
    setEnabled(s.review_threshold_cents !== null);
    setThreshold(s.review_threshold_cents !== null ? (s.review_threshold_cents / 100).toFixed(2) : '');
    setFundsPolicy(s.funds_policy);
    setShipWindow(s.held_ship_window);
  };

  useEffect(() => {
    api
      .get<{ settings: OrderReviewSettings }>('/api/admin/settings/order-review')
      .then((d) => apply(d.settings))
      .catch((err) => setMessage({ text: err.message, error: true }));
  }, []);

  const save = async () => {
    let cents: number | null = null;
    if (enabled) {
      const n = Number(threshold);
      if (!threshold.trim() || !Number.isFinite(n) || n < 0) {
        setMessage({ text: 'Enter the order amount that needs approval, in dollars.', error: true });
        return;
      }
      cents = Math.round(n * 100);
    }
    setBusy(true);
    setMessage(null);
    try {
      const d = await api.put<{ settings: OrderReviewSettings }>('/api/admin/settings/order-review', {
        review_threshold_cents: cents,
        funds_policy: fundsPolicy,
        held_ship_window: shipWindow,
      });
      apply(d.settings);
      setMessage({ text: 'Saved. Applies to orders placed from now on.', error: false });
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Save failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  if (!saved) return <p>{message?.text ?? 'Loading…'}</p>;

  const reviewOff = !enabled;
  const effectivePolicy = reviewOff && fundsPolicy === 'review' ? 'never (no approval amount set)' : null;

  return (
    <div style={{ maxWidth: 720 }}>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400 }}>Settings</h1>

      <section style={card}>
        <h2 style={h2}>Order review</h2>
        <p style={muted}>
          Held orders aren't sent to Gelato until they clear. The customer gets a calm "your order is being
          processed" email with a slightly longer ship time instead of the standard confirmation — nothing
          about payment or review.
        </p>

        <label style={{ ...row, marginTop: 16 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>Require approval for orders of</span>
          <span style={{ color: '#6b7280' }}>$</span>
          <input
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            disabled={!enabled}
            inputMode="decimal"
            placeholder="500.00"
            style={{ ...input, width: 100 }}
          />
          <span>or more</span>
        </label>
        {enabled && threshold && Number(threshold) >= 0 && (
          <p style={{ ...muted, margin: '4px 0 0 24px' }}>
            Orders totalling {formatUsd(Math.round(Number(threshold) * 100))} or more (including shipping and tax)
            wait for you to approve them on the order page.
          </p>
        )}

        <div style={{ marginTop: 20 }}>
          <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 6 }}>Wait for the payment to clear in Stripe</div>
          {FUNDS_OPTIONS.map((o) => (
            <label key={o.value} style={{ display: 'block', margin: '6px 0', fontSize: 14 }}>
              <input
                type="radio"
                name="funds"
                checked={fundsPolicy === o.value}
                onChange={() => setFundsPolicy(o.value)}
              />{' '}
              {o.label}
              <div style={{ ...muted, margin: '2px 0 0 22px' }}>{o.help}</div>
            </label>
          ))}
          {effectivePolicy && (
            <p style={{ fontSize: 12, color: '#b45309', margin: '4px 0 0' }}>
              With no approval amount, this works out to: {effectivePolicy}.
            </p>
          )}
          <p style={{ ...muted, marginTop: 8 }}>
            "Cleared" means the charge's funds are available in your Stripe balance. Held orders are rechecked
            every hour and sent to Gelato automatically once everything has cleared.
          </p>
        </div>

        <label style={{ display: 'block', marginTop: 20 }}>
          <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 6 }}>Ship time quoted to held orders</div>
          <input
            value={shipWindow}
            onChange={(e) => setShipWindow(e.target.value)}
            maxLength={60}
            style={{ ...input, width: 260 }}
          />
          <div style={{ ...muted, marginTop: 4 }}>
            Shown as "we expect it to ship within <em>{shipWindow || '…'}</em>."
          </div>
        </label>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
          <button type="button" onClick={save} disabled={busy} style={btnPrimary}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {message && (
            <span style={{ fontSize: 13, color: message.error ? '#b91c1c' : '#15803d' }}>{message.text}</span>
          )}
          {saved.updated_at && !message && (
            <span style={muted}>
              Last changed {formatDate(saved.updated_at)}
              {saved.updated_by ? ` by ${saved.updated_by}` : ''}
            </span>
          )}
        </div>
      </section>

      <GallerySpacing />
    </div>
  );
}

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e5e5',
  borderRadius: 8,
  padding: 24,
  marginTop: 16,
};
const h2: React.CSSProperties = { fontFamily: 'Georgia, serif', fontSize: 20, fontWeight: 400, margin: 0 };
const muted: React.CSSProperties = { fontSize: 12, color: '#6b7280', margin: '6px 0 0' };
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 };
const input: React.CSSProperties = {
  padding: '6px 8px',
  border: '1px solid #e5e5e5',
  borderRadius: 6,
  fontSize: 14,
  boxSizing: 'border-box',
};
const btnPrimary: React.CSSProperties = {
  padding: '10px 16px',
  background: '#1a1a1a',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 14,
  cursor: 'pointer',
};
